import React, { useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const KIND_OPTIONS = [
  { value: "vip_creator_combo", label: "VIP + Creator (begge deler)" },
  { value: "vip", label: "Kun VIP" },
  { value: "creator_vip", label: "Kun Creator (gratis creator-tid)" },
];

const DAY_PRESETS = [7, 30, 90, 180, 365];

export default function AdminGrantTier() {
  const [username, setUsername] = useState("");
  const [kind, setKind] = useState("vip_creator_combo");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!username.trim()) {
      toast.error("Skriv inn brukernavn");
      return;
    }
    if (!days || days < 1) {
      toast.error("Antall dager må være minst 1");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/users/grant-tier", {
        username: username.trim(),
        kind,
        days: Number(days),
      });
      const g = r.data?.granted || {};
      const nice = kind === "vip_creator_combo"
        ? "VIP + Creator"
        : kind === "vip" ? "VIP" : "Creator";
      toast.success(`${nice} gitt til ${username} (${days} dager).`);
      setResult({
        username,
        kind: nice,
        days,
        tier_until: g.tier_until,
        creator_vip_until: g.creator_vip_until,
        is_creator: g.is_creator,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("no-NO", { day: "2-digit", month: "long", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="border border-white/10 p-5 mb-8" data-testid="admin-grant-tier-block">
      <div className="flex items-center gap-2 mb-3">
        <Gift className="h-4 w-4 text-[var(--magenta)]" />
        <h2 className="font-fraunces italic text-xl text-[var(--cream)]">Gi VIP / Creator direkte (uten kode)</h2>
      </div>
      <p className="font-manrope text-xs text-[var(--cream-soft)] mb-4 leading-relaxed">
        For brukere som ikke fikk aktivert kode ved registrering, eller som du vil gi VIP/Creator som gave/kompensasjon.
        Legger til X dager på eksisterende periode (starter ny hvis utløpt).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block font-meta text-[10px] tracking-widest uppercase text-[var(--cream-faint)] mb-1">Brukernavn</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="f.eks. Inkedbarbie"
            className="input-editorial w-full"
            data-testid="grant-username"
          />
        </div>
        <div>
          <label className="block font-meta text-[10px] tracking-widest uppercase text-[var(--cream-faint)] mb-1">Hva skal gis</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="input-editorial w-full"
            data-testid="grant-kind"
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-meta text-[10px] tracking-widest uppercase text-[var(--cream-faint)] mb-1">Antall dager</label>
          <div className="flex gap-1">
            <input
              type="number"
              min="1"
              max="3650"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="input-editorial w-24"
              data-testid="grant-days"
            />
            <div className="flex gap-1 flex-wrap">
              {DAY_PRESETS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={`px-2 py-1 text-xs border ${days === d ? "border-[var(--magenta)] text-[var(--magenta)]" : "border-white/10 text-[var(--cream-soft)] hover:border-white/30"}`}
                  data-testid={`grant-days-${d}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={submit}
        disabled={busy}
        className="px-5 py-2.5 bg-[var(--magenta)] text-black font-meta text-xs tracking-widest uppercase disabled:opacity-50 inline-flex items-center gap-2"
        data-testid="grant-submit"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
        Gi tilgang
      </button>

      {result && (
        <div className="mt-4 border-l-2 border-emerald-400 pl-3 py-2 font-manrope text-sm text-[var(--cream)]" data-testid="grant-result">
          <div className="font-meta text-xs text-emerald-300 mb-1">Gjennomført</div>
          <div><span className="text-[var(--cream-faint)]">Bruker:</span> {result.username}</div>
          <div><span className="text-[var(--cream-faint)]">Gitt:</span> {result.kind} · {result.days} dager</div>
          {result.tier_until && (
            <div><span className="text-[var(--cream-faint)]">VIP til:</span> {fmtDate(result.tier_until)}</div>
          )}
          {result.creator_vip_until && (
            <div><span className="text-[var(--cream-faint)]">Creator-VIP til:</span> {fmtDate(result.creator_vip_until)}</div>
          )}
        </div>
      )}
    </div>
  );
}
